import { SelectCapitoli } from "@/components/SelectCapitoli/SelectCapitoli";
import { SelectFiles } from "@/components/SelectFiles/SelectFiles";
import { Form, FormField } from "@/components/ui";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui";
import { APP_LOCAL_DATE_FORMAT } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoChapter } from "@/shared/model/edo-chapter.model";
import {
  IEdoCourseMaterial,
  defaultValue,
} from "@/shared/model/edo-course-material.model";
import { IEdoCourse } from "@/shared/model/edo-course.model";
import { IEdoFile } from "@/shared/model/edo-file.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import {
  createMany,
  updateEntity,
} from "@/shared/reducers/entities/edo-course-material.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { getFileAvatar } from "@/shared/util/file-avatar-utils";
import { Check, X } from "lucide-react";
import axios from "axios";
import "moment/locale/it";
import React, { useEffect, useState } from "react";
import { TextFormat, translate } from "react-jhipster";
import { z } from "zod";

const titleSchema = z.object({
  title: z.string().min(1, translate("edocendoApp.edoCourseMaterial.title") + " required"),
});

interface EditCourseMaterialProps {
  fixedCourses?: IEdoCourse[];
  courseMaterial?: IEdoCourseMaterial;
  open: boolean;
  onClose: () => void;
}

const EditCourseMaterial = (props: EditCourseMaterialProps) => {
  const dispatch = useAppDispatch();
  const { loading, updating, updateSuccess } = useAppSelector<
    EntityState<IEdoCourseMaterial>
  >((state) => state.edoCourseMaterial);
  const { account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );
  const [form, setForm] = useState<IEdoCourseMaterial>({
    ...defaultValue,
    ...props.courseMaterial,
  });
  const [tabValue, setTabValue] = useState("info");
  const [selectedFiles, setSelectedFiles] = useState<IEdoFile[]>([]);
  const [selectedChapterLocal, setSelectedChapterLocal] =
    useState<IEdoChapter | null>(null);
  const [availableCourses, setAvailableCourses] = useState<IEdoCourse[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<IEdoCourse[]>([]);

  const { courseMaterial, open, onClose: handleClose, fixedCourses } = props;
  const isNew = props.courseMaterial == null || props.courseMaterial === undefined;

  const handleSelectedFiles = (fileList: IEdoFile[]) => {
    setSelectedFiles(fileList);
  };

  const handleCloseDialog = () => {
    setForm({ ...defaultValue });
    handleClose();
  };

  useEffect(() => {
    setForm({ ...defaultValue, ...courseMaterial });
    if (courseMaterial?.chapter)
      setSelectedChapterLocal(courseMaterial.chapter);
  }, [courseMaterial]);

  useEffect(() => {
    if (updateSuccess) {
      handleCloseDialog();
    }
  }, [updateSuccess]);

  useEffect(() => {
    const getTeachersCourses = async () => {
      const response = await axios.get<{ data: IEdoCourse[] }>(
        `/teachers/${account?.id}/courses`
      );
      setAvailableCourses(response.data.data ?? []);
    };
    const getCourseMaterialFiles = async () => {
      if (!courseMaterial?.id) return;
      const response = await axios.get(`/course-materials/${courseMaterial.id}/files`);
      setSelectedFiles(response.data.data ?? []);
    };
    if (!open) {
      setForm({ ...defaultValue });
      setSelectedFiles([]);
      setTabValue("info");
      setSelectedChapterLocal(null);
    } else {
      getTeachersCourses();
      if (courseMaterial?.id) getCourseMaterialFiles();
    }
  }, [open, account?.id, courseMaterial?.id]);

  useEffect(() => {
    if (fixedCourses?.length && availableCourses.length) {
      const toAdd = availableCourses.filter(
        (course) =>
          fixedCourses.some((fc) => fc.id === course.id) &&
          !selectedCourses.some((sc) => sc.id === course.id)
      );
      if (toAdd.length) setSelectedCourses((prev) => [...prev, ...toAdd]);
    }
  }, [fixedCourses, availableCourses]);

  const removeFile = (index: number) => {
    const next = Array.from(selectedFiles);
    next.splice(index, 1);
    setSelectedFiles(next);
  };

  const toggleCourse = (course: IEdoCourse) => {
    const isFixed = fixedCourses?.some((c) => c.id === course.id);
    if (isFixed) return;
    setSelectedCourses((prev) =>
      prev.some((c) => c.id === course.id)
        ? prev.filter((c) => c.id !== course.id)
        : [...prev, course]
    );
  };

  const saveEntity = (data?: { title?: string }) => {
    const payload = { ...form, ...(data?.title != null && { title: data.title }) };
    if (isNew) {
      dispatch(
        createMany({
          ...payload,
          files: selectedFiles,
          chapter: selectedChapterLocal,
          courses: selectedCourses,
        })
      );
    } else {
      dispatch(
        updateEntity({
          ...payload,
          files: selectedFiles,
          chapter: selectedChapterLocal,
        })
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleCloseDialog()}>
      <DialogContent
        showClose={false}
        className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:max-h-[85vh]"
      >
        <DialogHeader className="shrink-0 flex-row items-center justify-between gap-4 border-b border-[var(--color-mockup-border)] px-6 py-4">
          <DialogTitle>
            {isNew
              ? translate("edocendoApp.edoCourseMaterial.home.createLabel")
              : translate("edocendoApp.edoCourseMaterial.home.editLabel")}
          </DialogTitle>
          <div className="flex items-center gap-1">
            <IconButton
              disabled={updating}
              onClick={handleCloseDialog}
              aria-label="Chiudi"
            >
              <X className="h-4 w-4" />
            </IconButton>
            <IconButton
              disabled={updating}
              type="submit"
              form="edit-course-material-form"
              aria-label="Salva"
            >
              <Check className="h-4 w-4" />
            </IconButton>
          </div>
        </DialogHeader>
        <Tabs
          value={tabValue}
          onValueChange={setTabValue}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <TabsList className="mx-6 mt-4 shrink-0 self-start">
            <TabsTrigger value="info">Informazioni</TabsTrigger>
            <TabsTrigger value="docs">Documenti</TabsTrigger>
            <TabsTrigger value="chapter">Capitolo</TabsTrigger>
          </TabsList>
          <TabsContent
            value="info"
            className="mt-0 min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-5"
          >
            {loading ? (
              <div className="flex justify-center py-4">
                <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : (
              <Form
                id="edit-course-material-form"
                schema={titleSchema}
                onSubmit={(data) => saveEntity(data)}
                defaultValues={{ title: form.title || "" }}
                className="flex flex-col gap-4"
              >
                <FormField
                  name="title"
                  label={translate("edocendoApp.edoCourseMaterial.title")}
                />
                {isNew && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
                      Corsi
                    </label>
                    <div className="max-h-40 overflow-y-auto rounded-2xl border border-[var(--color-mockup-border)]">
                      {availableCourses.map((course) => {
                        const isFixed = fixedCourses?.some((c) => c.id === course.id);
                        const checked = selectedCourses.some((c) => c.id === course.id);
                        return (
                          <label
                            key={course.id}
                            className={`flex cursor-pointer items-center gap-2 border-b border-[var(--color-mockup-border)] px-3 py-2 last:border-0 dark:border-[var(--color-mockup-border)] ${
                              isFixed ? "cursor-not-allowed opacity-70" : "hover:bg-primary/5"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={isFixed}
                              onChange={() => toggleCourse(course)}
                              className="h-4 w-4 rounded-sm border-[var(--color-mockup-border)] text-primary focus:ring-primary"
                            />
                            <span className="text-sm">
                              {course.subject?.name} - {course.class?.year?.year}°
                              {course.class?.section} ({course.class?.school?.name})
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Form>
            )}
          </TabsContent>
          <TabsContent
            value="docs"
            className="mt-0 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 pb-6 pt-5"
          >
            <SelectFiles
              selectedFiles={selectedFiles}
              setSelectedFiles={handleSelectedFiles}
              enableUpload={true}
            />
            <div className="overflow-hidden rounded-2xl border border-[var(--color-mockup-border)]">
              <div className="border-b border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 px-4 py-2 text-sm font-medium text-[var(--color-mockup-text-mid)] dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/50 dark:text-[var(--color-mockup-text-light)]">
                Documenti selezionati
              </div>
              {selectedFiles?.length > 0 ? (
                <List>
                  {selectedFiles.map((file: IEdoFile, index: number) => (
                    <ListItem
                      key={file.id}
                      className="flex items-center gap-3 px-4 py-2"
                    >
                      <ListItemIcon className="shrink-0">
                        {getFileAvatar(file.fileType)}
                      </ListItemIcon>
                      <ListItemText
                        primary={file.originalFileName}
                        secondary={
                          <TextFormat
                            value={file.createdAt}
                            type="date"
                            format={APP_LOCAL_DATE_FORMAT}
                          />
                        }
                        className="min-w-0 flex-1 truncate text-sm"
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          onClick={() => removeFile(index)}
                          aria-label="Rimuovi file"
                        >
                          <X className="h-4 w-4" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <p className="p-4 text-body text-[var(--color-mockup-text-mid)]">
                  Nessun file selezionato.
                </p>
              )}
            </div>
          </TabsContent>
          <TabsContent
            value="chapter"
            className="mt-0 min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-5"
          >
            <SelectCapitoli
              multiple={false}
              selectedChapter={selectedChapterLocal}
              setSelectedChapter={setSelectedChapterLocal}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default EditCourseMaterial;
